import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import api from '../utils/api';

const GRADE_COLORS = { A: 'var(--neon-green)', B: 'var(--neon-cyan)', C: 'var(--neon-yellow)', D: 'var(--neon-pink)' };
const CATEGORY_COLORS = { energy: 'var(--neon-orange)', offsets: 'var(--neon-green)', supply: 'var(--neon-cyan)', strategy: 'var(--neon-yellow)', packaging: 'var(--neon-pink)', logistics: 'var(--neon-blue)', data: 'var(--muted2)' };
const SCOPE_COLORS = { 1: 'var(--neon-orange)', 2: 'var(--neon-yellow)', 3: 'var(--neon-cyan)' };
const PERIODS = ['2025-Q1', '2025-Q2', '2025-Q3', '2025-Q4', '2026-Q1', '2026-Q2'];

export default function Emissions() {
  const [score, setScore] = useState(null);
  const [summary, setSummary] = useState(null);
  const [factors, setFactors] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');

  // Form state
  const [form, setForm] = useState({
    period: '2025-Q4', scope: 1, ghg_category: '', activity: '', quantity: '', unit: '', site: '', notes: '',
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/emissions/score').then((r) => setScore(r.data)).catch(() => {}),
      api.get('/emissions/summary').then((r) => setSummary(r.data)).catch(() => {}),
      api.get('/emissions/factors').then((r) => setFactors(r.data.factors || [])).catch(() => {}),
      api.get('/emissions/recommendations').then((r) => setRecommendations(r.data.recommendations || [])).catch(() => {}),
      api.get('/emissions/my').then((r) => setRecentLogs((r.data.logs || []).slice(0, 5))).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Filter factors by selected scope then by category
  const filteredFactors = useMemo(() => {
    let f = factors.filter((fac) => fac.scope === parseInt(form.scope, 10));
    if (form.ghg_category) f = f.filter((fac) => fac.ghg_category === form.ghg_category);
    return f;
  }, [factors, form.scope, form.ghg_category]);

  const uniqueCategories = useMemo(() => {
    const cats = factors.filter((f) => f.scope === parseInt(form.scope, 10)).map((f) => f.ghg_category);
    return [...new Set(cats)];
  }, [factors, form.scope]);

  const selectedFactor = useMemo(() => factors.find((f) => f.activity === form.activity), [factors, form.activity]);

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'scope') { next.ghg_category = ''; next.activity = ''; next.unit = ''; }
      if (field === 'ghg_category') { next.activity = ''; next.unit = ''; }
      if (field === 'activity') {
        const fac = factors.find((f) => f.activity === value);
        next.unit = fac?.unit || '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.activity) { setError('Please select an activity.'); return; }
    if (!form.quantity || parseFloat(form.quantity) <= 0) { setError('Quantity must be positive.'); return; }
    setSubmitting(true);
    try {
      await api.post('/emissions/log', { ...form, scope: parseInt(form.scope, 10), quantity: parseFloat(form.quantity) });
      setSuccess('Emission activity logged successfully. Your carbon score has been updated.');
      setForm((prev) => ({ ...prev, quantity: '', notes: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log emission.');
    } finally {
      setSubmitting(false);
    }
  };

  // Category breakdown chart data
  const chartData = useMemo(() => {
    if (!summary?.category_breakdown) return [];
    return summary.category_breakdown
      .filter((c) => scopeFilter === 'all' || c.scope === parseInt(scopeFilter, 10))
      .slice(0, 10)
      .map((c) => ({ name: c.category.replace('Scope \\d+ - ', '').slice(0, 24), tco2e: c.tco2e, scope: c.scope }));
  }, [summary, scopeFilter]);

  const gradeColor = score ? GRADE_COLORS[score.grade] : 'var(--muted2)';
  const latestPeriod = summary?.periods?.[0];
  const roiBadgeColor = (m) => m === 0 ? 'var(--neon-green)' : m <= 6 ? 'var(--neon-green)' : m <= 18 ? 'var(--neon-yellow)' : 'var(--neon-pink)';

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Emission Ledger — Emissions Tracker</div>
        <h1>Carbon Footprint Dashboard</h1>
        <p className="section-copy">Measure Scope 1, 2, and 3 emissions across all activities. Your data powers the carbon score.</p>
      </section>

      {/* ── Carbon Score Hero ── */}
      <section className="surface-panel glow-surface" style={{ padding: '1.75rem', borderTop: `3px solid ${gradeColor}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr 1fr', gap: '2rem', alignItems: 'center' }}>
          {/* Score ring (CSS-only) */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '140px', height: '140px', borderRadius: '50%',
              border: `8px solid ${gradeColor}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', boxShadow: `0 0 24px ${gradeColor}40`,
            }}>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '2.2rem', fontWeight: 700, color: gradeColor, lineHeight: 1 }}>
                {score?.score ?? '—'}
              </div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted2)' }}>/100</div>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <span className="badge" style={{ color: gradeColor, borderColor: gradeColor }}>Grade {score?.grade || '?'}</span>
            </div>
          </div>
          {/* Scope 1 */}
          <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '2rem' }}>
            <div className="stat-label">Scope 1 — Direct</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: 'var(--neon-orange)', marginTop: '0.25rem' }}>
              {score?.scope1_tco2e ?? latestPeriod?.scope1 ?? '—'} <span style={{ fontSize: '0.8rem', color: 'var(--muted2)' }}>tCO2e</span>
            </div>
            <div className="stat-sub">Combustion, mobile, fugitive</div>
          </div>
          {/* Scope 2 */}
          <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '2rem' }}>
            <div className="stat-label">Scope 2 — Electricity</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: 'var(--neon-yellow)', marginTop: '0.25rem' }}>
              {score?.scope2_tco2e ?? latestPeriod?.scope2 ?? '—'} <span style={{ fontSize: '0.8rem', color: 'var(--muted2)' }}>tCO2e</span>
            </div>
            <div className="stat-sub">Purchased electricity & steam</div>
          </div>
          {/* Scope 3 */}
          <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '2rem' }}>
            <div className="stat-label">Scope 3 — Value chain</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.6rem', color: 'var(--neon-cyan)', marginTop: '0.25rem' }}>
              {score?.scope3_tco2e ?? latestPeriod?.scope3 ?? '—'} <span style={{ fontSize: '0.8rem', color: 'var(--muted2)' }}>tCO2e</span>
            </div>
            <div className="stat-sub">Logistics, suppliers, travel</div>
          </div>
        </div>

        {/* Data completeness bar */}
        {score && (
          <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <span className="stat-label">Data completeness</span>
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: score.data_completeness_pct >= 70 ? 'var(--neon-green)' : 'var(--neon-yellow)' }}>
                {score.data_completeness_pct}%
              </span>
            </div>
            <div style={{ height: '6px', borderRadius: '3px', background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${score.data_completeness_pct}%`, background: score.data_completeness_pct >= 70 ? 'var(--neon-green)' : 'var(--neon-yellow)', borderRadius: '3px', transition: 'width 0.5s' }} />
            </div>
            {score.data_completeness_pct < 50 && (
              <p style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-yellow)', marginTop: '0.4rem' }}>
                ⚠ Log more activities for an accurate score
              </p>
            )}
          </div>
        )}
      </section>

      {/* ── GHG Category Breakdown Chart ── */}
      {chartData.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-stack">
              <div className="section-tag">GHG category breakdown</div>
              <h2 style={{ fontSize: '1.1rem' }}>Emissions by category — {summary?.latest_period}</h2>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {['all', '1', '2', '3'].map((s) => (
                <button key={s} type="button"
                  className={`btn btn-sm ${scopeFilter === s ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setScopeFilter(s)} style={{ padding: '0.25rem 0.65rem' }}>
                  {s === 'all' ? 'All' : `Scope ${s}`}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <XAxis type="number" tick={{ fontFamily: 'Space Mono', fontSize: 9, fill: '#5a5e72' }} />
              <YAxis type="category" dataKey="name" tick={{ fontFamily: 'Space Mono', fontSize: 9, fill: '#8a8ea8' }} width={160} />
              <Tooltip contentStyle={{ background: '#0d0f1a', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'Space Mono', fontSize: '0.7rem', borderRadius: '8px' }}
                formatter={(v) => [`${v} tCO2e`, 'Emissions']} />
              <Bar dataKey="tco2e" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={SCOPE_COLORS[entry.scope] || 'var(--neon-cyan)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* ── Hotspot table ── */}
      {summary?.category_breakdown?.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-tag">Emission hotspots</div>
            <span className="stat-label">Top sources by tCO2e</span>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Category</th><th>Scope</th><th>tCO2e</th><th>% of Total</th><th>Action</th></tr>
            </thead>
            <tbody>
              {summary.category_breakdown.slice(0, 5).map((c, i) => {
                const total = summary?.periods?.[0]?.total || 1;
                const pct = Math.round((c.tco2e / total) * 100);
                return (
                  <tr key={i}>
                    <td style={{ color: 'var(--text)' }}>{c.category}</td>
                    <td><span className="badge" style={{ color: SCOPE_COLORS[c.scope], borderColor: SCOPE_COLORS[c.scope] }}>Scope {c.scope}</span></td>
                    <td style={{ color: 'var(--neon-orange)', fontFamily: 'Space Mono, monospace' }}>{c.tco2e}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '60px', height: '4px', borderRadius: '2px', background: 'var(--border)' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: SCOPE_COLORS[c.scope], borderRadius: '2px' }} />
                        </div>
                        <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--muted2)' }}>{pct}%</span>
                      </div>
                    </td>
                    <td>
                      <Link to={c.scope === 3 ? '/supply-chain' : '/emissions'} className="btn btn-secondary btn-sm">Reduce</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* ── Recommendations ── */}
      {recommendations.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
            <div className="section-tag">Reduction recommendations</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
            {recommendations.map((rec, i) => (
              <div key={i} style={{ padding: '1.25rem', borderLeft: `3px solid ${CATEGORY_COLORS[rec.category] || 'var(--neon-cyan)'}`, background: 'rgba(255,255,255,0.02)', borderRadius: '0 8px 8px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <span className="badge" style={{ color: CATEGORY_COLORS[rec.category], borderColor: CATEGORY_COLORS[rec.category], textTransform: 'capitalize', fontSize: '0.6rem' }}>{rec.category}</span>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: roiBadgeColor(rec.roi_months), padding: '0.15rem 0.4rem', border: `1px solid ${roiBadgeColor(rec.roi_months)}`, borderRadius: '999px' }}>
                    {rec.roi_months === 0 ? 'Immediate' : `${rec.roi_months}mo ROI`}
                  </span>
                </div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', lineHeight: 1.45, marginBottom: '0.75rem' }}>{rec.text}</div>
                <Link to={rec.link} className="btn btn-secondary btn-sm">Take action →</Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Log New Activity Form ── */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Log emission activity</div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">Period *</label>
              <select className="form-input" value={form.period} onChange={(e) => handleFormChange('period', e.target.value)}>
                {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Scope *</label>
              <select className="form-input" value={form.scope} onChange={(e) => handleFormChange('scope', parseInt(e.target.value, 10))}>
                <option value={1}>Scope 1 — Direct emissions</option>
                <option value={2}>Scope 2 — Electricity</option>
                <option value={3}>Scope 3 — Value chain</option>
              </select>
            </div>
            <div>
              <label className="form-label">GHG Category</label>
              <select className="form-input" value={form.ghg_category} onChange={(e) => handleFormChange('ghg_category', e.target.value)}>
                <option value="">All categories</option>
                {uniqueCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Activity *</label>
              <select className="form-input" value={form.activity} onChange={(e) => handleFormChange('activity', e.target.value)}>
                <option value="">Select activity…</option>
                {filteredFactors.map((f) => <option key={f.activity} value={f.activity}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Quantity *</label>
              <input className="form-input" type="number" min="0" step="any" placeholder="e.g. 1000"
                value={form.quantity} onChange={(e) => handleFormChange('quantity', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Unit</label>
              <input className="form-input" type="text" readOnly value={form.unit || (selectedFactor?.unit || '')} placeholder="auto-filled" style={{ opacity: 0.6 }} />
            </div>
            <div>
              <label className="form-label">Site / Location</label>
              <input className="form-input" type="text" placeholder="e.g. Main Factory" value={form.site} onChange={(e) => handleFormChange('site', e.target.value)} />
            </div>
          </div>

          {selectedFactor && (
            <div style={{ padding: '0.75rem', background: 'rgba(0,255,136,0.05)', borderRadius: '8px', border: '1px solid rgba(0,255,136,0.15)', marginBottom: '1rem' }}>
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--neon-green)' }}>
                Emission factor: {selectedFactor.factor_kgco2e} kg CO2e per {selectedFactor.unit}
                {form.quantity > 0 ? ` → ${Math.round(form.quantity * selectedFactor.factor_kgco2e / 100) / 10} tCO2e` : ''}
                {' '}· Source: {selectedFactor.source}
              </span>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" rows={2} placeholder="e.g. Boiler fuel Q4 — from utility bill"
              value={form.notes} onChange={(e) => handleFormChange('notes', e.target.value)} style={{ resize: 'vertical' }} />
          </div>

          {error && <div className="form-error" style={{ marginBottom: '0.75rem', color: 'var(--neon-pink)' }}>{error}</div>}
          {success && <div style={{ marginBottom: '0.75rem', color: 'var(--neon-green)', fontFamily: 'Space Mono, monospace', fontSize: '0.75rem' }}>{success}</div>}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Logging…' : 'Log emission activity'}
          </button>
        </form>
      </section>

      {/* ── Recent Log Entries ── */}
      {recentLogs.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-tag">Recent entries</div>
            <Link to="/emissions/log" className="btn btn-secondary btn-sm">View all logs</Link>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Period</th><th>Scope</th><th>Activity</th><th>Quantity</th><th>tCO2e</th><th>Site</th></tr>
            </thead>
            <tbody>
              {recentLogs.map((l) => (
                <tr key={l.id}>
                  <td style={{ color: 'var(--muted2)', fontFamily: 'Space Mono, monospace', fontSize: '0.72rem' }}>{l.period}</td>
                  <td><span className="badge" style={{ color: SCOPE_COLORS[l.scope], borderColor: SCOPE_COLORS[l.scope] }}>Scope {l.scope}</span></td>
                  <td style={{ color: 'var(--text)' }}>{l.label || l.activity}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--muted2)' }}>{l.quantity?.toLocaleString('en-IN')} {l.unit}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-orange)' }}>{(l.total_kgco2e / 1000).toFixed(2)}</td>
                  <td style={{ color: 'var(--muted2)', fontSize: '0.78rem' }}>{l.site || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
